import type { FC } from "react"
import { useMemo, useState } from "react"

import { useUpsertGrCostBreakdown } from "../hooks/useUpsertGrCostBreakdown"
import type { CostBreakdownFormValues, CostBreakdownProductsFormValues } from "../schemas"
import { CostBreakdownDialogType } from "../types"
import { filterProductsWithoutWarehouseAssociation } from "../utils/filterProductsWithoutWarehouseAssociation"
import { getCostBreakdownBaseFormValues } from "../utils/formDefaults"
import { transformFormDataToPayload } from "../utils/transformFormDataToPayload"
import { transformGoodsReceiptToFormData } from "../utils/transformGoodsReceiptToFormData"

import { CostBreakdownDialog } from "./CostBreakdownDialog"
import { CostBreakdownIncompleteDialog } from "./CostBreakdownIncompleteDialog"
import { CostBreakdownProductsDialog } from "./CostBreakdownProductsDialog"

import { CancelDialog } from "@/components/common/Modals"
import type { GoodsReceipt, Product } from "@/graphql/codegen/graphql"

interface CostBreakdownDialogsProps {
  goodsReceipt: GoodsReceipt
  purchaseOrderLineId?: string
  onClose: () => void
  onSubmit: () => void
}

export const CostBreakdownDialogs: FC<CostBreakdownDialogsProps> = ({
  goodsReceipt,
  purchaseOrderLineId,
  onClose,
  onSubmit,
}) => {
  const { upsertGrCostBreakdown, isUpserting } = useUpsertGrCostBreakdown()

  // Determine edit mode based on presence of cost breakdown data
  const isEditMode = !!goodsReceipt.costBreakdown

  // Track if user has made ANY changes during the entire dialog flow
  const [hasUserMadeAnyChanges, setHasUserMadeAnyChanges] = useState(false)

  const [productsWithoutWarehouseAssociation, setProductsWithoutWarehouseAssociation] = useState<Product[]>([])

  // Initialize data for edit mode
  const { initialGrCostBreakdown, initialProductsCostBreakdown } = useMemo(() => {
    if (!isEditMode || !goodsReceipt.costBreakdown) {
      return { initialGrCostBreakdown: undefined, initialProductsCostBreakdown: undefined }
    }

    try {
      const { grCostBreakdown, productsCostBreakdown } = transformGoodsReceiptToFormData(goodsReceipt)
      return {
        initialGrCostBreakdown: grCostBreakdown,
        initialProductsCostBreakdown: productsCostBreakdown,
      }
    } catch (error) {
      console.error("Failed to transform existing cost breakdown data:", error)
      return { initialGrCostBreakdown: undefined, initialProductsCostBreakdown: undefined }
    }
  }, [isEditMode, goodsReceipt])

  const [currentDialog, setCurrentDialog] = useState<CostBreakdownDialogType | null>(
    purchaseOrderLineId ? CostBreakdownDialogType.ProductsCostBreakdown : CostBreakdownDialogType.GrCostBreakdown
  )
  const [grCostBreakdown, setGrCostBreakdown] = useState<CostBreakdownFormValues | undefined>(
    purchaseOrderLineId && !initialGrCostBreakdown
      ? getCostBreakdownBaseFormValues(goodsReceipt)
      : initialGrCostBreakdown
  )
  const [productsCostBreakdown, setProductsCostBreakdown] = useState<CostBreakdownProductsFormValues | undefined>(
    initialProductsCostBreakdown
  )

  const closeForm = <T,>(setFormValues: (data: T) => void, isDirty: boolean, currentData?: T) => {
    if (isDirty) {
      setHasUserMadeAnyChanges(true)
    }

    if (isDirty || hasUserMadeAnyChanges) {
      if (currentData) {
        setFormValues(currentData)
      }
      setCurrentDialog(CostBreakdownDialogType.Cancel)
    } else {
      onClose()
    }
  }

  const handleGrFormClose = (isDirty: boolean, currentData?: CostBreakdownFormValues) =>
    closeForm(setGrCostBreakdown, isDirty, currentData)

  const handleProductsFormClose = (isDirty: boolean, currentData?: CostBreakdownProductsFormValues) =>
    closeForm(setProductsCostBreakdown, isDirty, currentData)

  const handleContinue = (data: CostBreakdownFormValues, formIsDirty: boolean) => {
    if (formIsDirty) {
      setHasUserMadeAnyChanges(true)
    }

    setGrCostBreakdown(data)

    if (isEditMode && formIsDirty) {
      const updatedProductsCostBreakdown = productsCostBreakdown
        ? {
            ...productsCostBreakdown,
            products: productsCostBreakdown.products.map((product) => ({
              ...product,
              allocatedGrCostPercentage: null,
              allocatedGrCostAmount: null,
              totalLaidDownCostPerUom: null,
            })),
          }
        : undefined
      setProductsCostBreakdown(updatedProductsCostBreakdown)
    }

    setCurrentDialog(CostBreakdownDialogType.ProductsCostBreakdown)
  }

  const handleGoBack = (currentProductsData?: CostBreakdownProductsFormValues) => {
    if (currentProductsData) {
      setProductsCostBreakdown(currentProductsData)
    }
    setCurrentDialog(CostBreakdownDialogType.GrCostBreakdown)
  }

  const handleSubmit = async (
    grCostBreakdown: CostBreakdownFormValues,
    productsCostBreakdown: CostBreakdownProductsFormValues
  ) => {
    const unassociatedProducts = filterProductsWithoutWarehouseAssociation(goodsReceipt, productsCostBreakdown.products)
    const unassociatedProductIds = new Set(unassociatedProducts.map((product) => product.productId))

    const input = transformFormDataToPayload({
      goodsReceiptId: goodsReceipt.goodsReceiptId,
      goodsReceipt: goodsReceipt,
      grCostBreakdown,
      productsCostBreakdown,
      productIdsWithoutWarehouseAssociation: unassociatedProductIds,
      isEditMode,
    })

    const isSuccess = await upsertGrCostBreakdown(input)

    if (!isSuccess) return

    if (unassociatedProducts.length) {
      setProductsWithoutWarehouseAssociation(unassociatedProducts)
      setCurrentDialog(CostBreakdownDialogType.Incomplete)
      return
    }

    onSubmit()
  }

  const handleCancelClose = () => {
    if (productsCostBreakdown) {
      setCurrentDialog(CostBreakdownDialogType.ProductsCostBreakdown)
    } else {
      setCurrentDialog(CostBreakdownDialogType.GrCostBreakdown)
    }
  }

  return (
    <>
      {currentDialog === CostBreakdownDialogType.GrCostBreakdown && (
        <CostBreakdownDialog
          goodsReceipt={goodsReceipt}
          grCostBreakdown={grCostBreakdown}
          onClose={handleGrFormClose}
          onSubmit={handleContinue}
        />
      )}

      {currentDialog === CostBreakdownDialogType.ProductsCostBreakdown && (
        <CostBreakdownProductsDialog
          goodsReceipt={goodsReceipt}
          grCostBreakdown={grCostBreakdown}
          productsCostBreakdown={productsCostBreakdown}
          purchaseOrderLineId={purchaseOrderLineId}
          isSubmitting={isUpserting}
          onCancel={handleProductsFormClose}
          onBack={handleGoBack}
          onSubmit={handleSubmit}
        />
      )}

      {currentDialog === CostBreakdownDialogType.Cancel && (
        <CancelDialog onClose={handleCancelClose} onConfirm={onClose} />
      )}

      {currentDialog === CostBreakdownDialogType.Incomplete && (
        <CostBreakdownIncompleteDialog
          goodsReceipt={goodsReceipt}
          products={productsWithoutWarehouseAssociation}
          onClose={onSubmit}
        />
      )}
    </>
  )
}
